import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getStartupOpportunities } from '../services/firebase/startup-opportunities';

/**
 * Custom hook for managing startup opportunities with filtering and search
 * Includes debounced search, pagination, and real-time filtering
 */
const useStartupOpportunities = (filters = {}, searchTerm = '') => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true); // Always start with loading true for immediate feedback
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Refs for managing debounced search and preventing race conditions
  const searchTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);
  const mountedRef = useRef(true);
  const lastVisibleRef = useRef(null);

  // Debounce delay for search
  const SEARCH_DEBOUNCE_DELAY = 300;

  // Track mounted state properly
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Fetch opportunities with current filters and search term
   * @param {boolean} isLoadMore - Whether this is a load more request
   * @param {boolean} isDebounced - Whether this is a debounced search request
   */
  const fetchOpportunities = useCallback(async (isLoadMore = false, isDebounced = false) => {
    try {
      // Cancel previous timeout if this is a new search
      if (!isLoadMore && searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set loading states
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else if (!isDebounced) {
        setLoading(true);
        setError(null);
      }

      // Prepare query options
      const queryOptions = {
        limit: 20,
        lastVisible: isLoadMore ? lastVisibleRef.current : null,
        companyStages: filters.companyStages || [],
        roleAreas: filters.roleAreas || [],
        experienceLevels: filters.experienceLevels || [],
        searchTerm: searchTerm.trim(),
        status: 'active',
        orderByField: 'createdAt',
        orderDirection: 'desc'
      };

      // Create request identifier to handle race conditions
      const requestId = Date.now();
      currentRequestRef.current = requestId;

      // Fetch data
      const result = await getStartupOpportunities(queryOptions);

      // Check if component is still mounted and this is the latest request
      if (!mountedRef.current || currentRequestRef.current !== requestId) {
        // Still need to reset loading state for cancelled requests
        if (mountedRef.current) {
          if (isLoadMore) {
            setIsLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
        return;
      }

      if (result.success) {
        const { opportunities: newOpportunities, lastVisible: newLastVisible, hasMore: newHasMore } = result.data;

        if (isLoadMore) {
          // Append new opportunities for load more
          setOpportunities(prev => [...prev, ...newOpportunities]);
        } else {
          // Replace opportunities for new search/filter
          setOpportunities(newOpportunities);
        }

        setLastVisible(newLastVisible);
        lastVisibleRef.current = newLastVisible;
        setHasMore(newHasMore);
        setError(null);
      } else {
        setError(result.error || 'Failed to fetch opportunities');
        if (!isLoadMore) {
          setOpportunities([]);
          setHasMore(false);
          setLastVisible(null);
          lastVisibleRef.current = null;
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message || 'An unexpected error occurred');
        if (!isLoadMore) {
          setOpportunities([]);
          setHasMore(false);
          setLastVisible(null);
          lastVisibleRef.current = null;
        }
      }
    } finally {
      if (mountedRef.current) {
        if (isLoadMore) {
          setIsLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, [filters.companyStages, filters.roleAreas, filters.experienceLevels, searchTerm]);

  /**
   * Debounced search function
   */
  const debouncedFetch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchOpportunities(false, true);
    }, SEARCH_DEBOUNCE_DELAY);
  }, [fetchOpportunities]);

  /**
   * Load more opportunities (pagination)
   */
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) {
      return Promise.resolve();
    }

    return fetchOpportunities(true);
  }, [fetchOpportunities, isLoadingMore, hasMore]);

  /**
   * Refresh opportunities (reset pagination and fetch from beginning)
   */
  const refresh = useCallback(() => {
    setLastVisible(null);
    lastVisibleRef.current = null;
    setHasMore(false);
    fetchOpportunities(false);
  }, [fetchOpportunities]);

  /**
   * Reset all state (useful when navigating away and back)
   */
  const reset = useCallback(() => {
    setOpportunities([]);
    setLoading(true);
    setError(null);
    setHasMore(false);
    setLastVisible(null);
    lastVisibleRef.current = null;
    setIsLoadingMore(false);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Effect for handling filter changes (immediate fetch)
  useEffect(() => {
    setLastVisible(null);
    lastVisibleRef.current = null;
    setHasMore(false);
    fetchOpportunities(false);
  }, [filters.companyStages, filters.roleAreas, filters.experienceLevels, fetchOpportunities]);

  // Effect for handling search term changes (debounced fetch)
  useEffect(() => {
    if (searchTerm !== undefined) {
      setLastVisible(null);
      lastVisibleRef.current = null;
      setHasMore(false);
      debouncedFetch();
    }
  }, [searchTerm, debouncedFetch]);

  // Initial fetch on mount - only runs once
  useEffect(() => {
    fetchOpportunities(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array intentional - should only run once on mount

  const hookReturnValue = {
    opportunities,
    loading,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
    refresh,
    reset,
    // Computed values
    isEmpty: !loading && opportunities.length === 0,
    hasResults: opportunities.length > 0,
  };

  return hookReturnValue;
};

export default useStartupOpportunities;